import classNames from 'classnames'
import type * as React from 'react'

import { Card, type CardTone } from 'partner360/components/card'

import { clampCount, type ResponsiveColumns } from '../../helpers/gridSizing'
import { getTileSizingStyle, type TileSizing } from '../../helpers/tileSizing'

import styles from './TileShell.module.scss'

type TileShellProps = {
  title: string
  subtitle?: string
  hideExpandButton?: boolean
  tone?: CardTone
  muted?: boolean
  highlighted?: boolean
  renderTitle?: (context: {
    title: string
    subtitle?: string
    expanded: boolean
    onToggle: () => void
  }) => React.ReactNode
  expanded: boolean
  onToggle: () => void
  /** How many grid columns/rows this tile occupies. Both default to 1. */
  colSpan?: number
  rowSpan?: number
  /** Height bounds inside the grid. Omit for the maximized overlay, which fills the board. */
  sizing?: TileSizing
  /** The grid's own column count per breakpoint tier, used to clamp `colSpan` so a
   *  tile never overflows the actual number of columns available. Omit for tiles that
   *  aren't laid out inside the grid (e.g. the maximized overlay). */
  gridColumns?: ResponsiveColumns
  /** Reports the root section element as it mounts/unmounts, so the board can measure
   *  this tile's on-screen rect (used by the 'stretch' expand animation). */
  onShellRef?: (el: HTMLElement | null) => void
  /** Marks the root as a widget-tour target. The board and its maximized overlay render
   *  the same tile twice, so each copy needs its own id — see TileConfig. */
  tourId?: string
  children: React.ReactNode
}

export const TileShell = ({
  title,
  subtitle,
  hideExpandButton = false,
  tone,
  muted = false,
  highlighted = false,
  renderTitle,
  expanded,
  onToggle,
  colSpan = 1,
  rowSpan = 1,
  sizing,
  gridColumns,
  onShellRef,
  tourId,
  children,
}: TileShellProps) => {
  const showExpandButton = !hideExpandButton
  const safeColSpan = clampCount(colSpan)
  const colSpanSm = gridColumns ? Math.min(safeColSpan, gridColumns.sm) : safeColSpan
  const colSpanLg = gridColumns ? Math.min(safeColSpan, gridColumns.lg) : safeColSpan
  const safeRowSpan = clampCount(rowSpan)

  return (
    <Card
      ref={onShellRef}
      aria-label={title}
      data-tour-id={tourId}
      /* A muted tile is read, not used: nothing in it takes a click, the keyboard or focus. */
      inert={muted || undefined}
      aria-disabled={muted || undefined}
      expanded={expanded}
      tone={tone}
      style={sizing ? getTileSizingStyle(sizing) : undefined}
      className={classNames(
        styles.tile,
        styles[`colSpan-sm-${colSpanSm}`],
        styles[`colSpan-lg-${colSpanLg}`],
        styles[`rowSpan-${safeRowSpan}`],
        {
          [styles['tile--expanded']]: expanded,
          [styles['tile--muted']]: muted,
          [styles['tile--highlighted']]: highlighted,
        }
      )}
      header={
        <>
          <div className={styles.heading}>
            {renderTitle ? (
              renderTitle({ title, subtitle, expanded, onToggle })
            ) : (
              <>
                <h3 className={styles.title}>{title}</h3>
                {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
              </>
            )}
          </div>

          {showExpandButton && (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={expanded ? `Minimize ${title}` : `Maximize ${title}`}
              className={styles.toggle}
            >
              <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
            </button>
          )}
        </>
      }
    >
      {children}
    </Card>
  )
}

TileShell.displayName = 'TileShell'
